import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  Check,
  CheckCircle2,
  ChevronDown,
  ChefHat,
  ImagePlus,
  Loader2,
  Save,
  ShieldCheck,
  Store,
  UserRound,
  Users,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import userManagementApi from "../../api/userManagementApi";
import resolveMediaUrl from "../../utils/resolveMediaUrl";
import {
  canManageUsers,
  getApiFieldErrors,
  getApiMessage,
  getFieldError,
  getUserEstablishmentId,
  getUserMode,
  translateFallback,
} from "./userManagementHelpers";
import "../../styles/users/user-form.css";

const managerEmptyForm = {
  role: "manager",
  name: "",
  email: "",
  phone: "",
  password: "",
  password_confirmation: "",

  // Kept internally only for edit mode. It is not shown in the form.
  establishment_id: "",

  // businesses table
  business_name: "",
  default_language: "fr",

  // establishments table
  business_type: "restaurant",
  business_email: "",
  business_phone: "",
  business_whatsapp: "",
  business_address: "",
  business_description: "",
  logo: null,
  logo_preview: "",
};

const staffEmptyForm = {
  name: "",
  email: "",
  phone: "",
  role: "waiter",
  password: "",
  password_confirmation: "",
};

const staffRoleOptions = [
  {
    value: "waiter",
    label: "Waiter",
    description: "Can create table orders and follow allowed order steps.",
    icon: Users,
  },
  {
    value: "cuisini",
    label: "Cuisini",
    description: "Can view kitchen orders and update preparation status.",
    icon: ChefHat,
  },
];

const managerRoleOption = {
  value: "manager",
  label: "Manager",
  description: "Business owner account that can manage menu, orders and staff.",
  icon: BriefcaseBusiness,
};

const salesRoleOption = {
  value: "salesrepresentative",
  label: "Sales Representative",
  description: "Visits establishments, registers clients and collects subscription and QR print orders.",
  icon: BriefcaseBusiness,
};

const businessTypeOptions = [
  { value: "restaurant", label: "Restaurant" },
  { value: "cafe", label: "Café" },
  { value: "snack", label: "Snack" },
  { value: "bakery", label: "Bakery" },
  { value: "hotel", label: "Hotel" },
  { value: "riad", label: "Riad" },
  { value: "other", label: "Other" },
];

const languageOptions = [
  { value: "fr", label: "Français" },
  { value: "ar", label: "العربية" },
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
];

const isBrowserFile = (value) => {
  return typeof File !== "undefined" && value instanceof File;
};

const getManagerEstablishment = (record = {}) => {
  return (
    record.establishment ||
    record.managed_establishment ||
    record.managedEstablishment ||
    record.restaurant ||
    record.business?.establishment ||
    record.business?.establishments?.[0] ||
    null
  );
};

const getManagerBusiness = (record = {}) => {
  return record.business || getManagerEstablishment(record)?.business || null;
};

const getManagerLogoPreview = (record = {}) => {
  const establishment = getManagerEstablishment(record);

  return resolveMediaUrl(
    establishment?.logo_url ||
      establishment?.logo ||
      establishment?.logo_path ||
      record.logo_url ||
      record.logo ||
      "",
  );
};

const getManagerFormFromRecord = (record = {}) => {
  const establishment = getManagerEstablishment(record);
  const business = getManagerBusiness(record);

  return {
    name: record.name || "",
    email: record.email || "",
    phone: record.phone || "",
    password: "",
    password_confirmation: "",

    establishment_id: record.establishment_id || establishment?.id || "",

    business_name:
      record.business_name ||
      business?.name ||
      establishment?.name ||
      "",

    default_language:
      establishment?.default_language ||
      business?.default_language ||
      record.default_language ||
      "fr",

    business_type:
      establishment?.type ||
      record.business_type ||
      "restaurant",

    business_email:
      establishment?.email ||
      record.business_email ||
      business?.email ||
      "",

    business_phone:
      establishment?.phone ||
      record.business_phone ||
      business?.phone ||
      "",

    business_whatsapp:
      establishment?.whatsapp ||
      record.business_whatsapp ||
      "",

    business_address:
      establishment?.address ||
      record.business_address ||
      "",

    business_description:
      establishment?.description ||
      record.business_description ||
      "",

    logo: null,
    logo_preview: getManagerLogoPreview(record),
  };
};

function PrettySelect({
  value,
  options = [],
  onChange,
  placeholder = "Select an option",
}) {
  const selectRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selectedOption = options.find(
    (option) => String(option.value) === String(value),
  );

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setOpen(false);
  };

  return (
    <div
      ref={selectRef}
      className={`users-form-pretty-select ${
        open ? "users-form-pretty-select--open" : ""
      }`}
    >
      <button
        type="button"
        className="users-form-pretty-select__button"
        onClick={() => setOpen((currentOpen) => !currentOpen)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selectedOption?.label || placeholder}</span>
        <ChevronDown size={17} />
      </button>

      {open && (
        <div className="users-form-pretty-select__options" role="listbox">
          {options.map((option) => {
            const active = String(option.value) === String(value);

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={active}
                className={`users-form-pretty-select__option ${
                  active ? "users-form-pretty-select__option--active" : ""
                }`}
                onClick={() => handleSelect(option.value)}
              >
                <span>{option.label}</span>

                {active && <Check size={15} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UserForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();

  const isEditMode = Boolean(userId);
  const mode = getUserMode(user);
  const salesPage = location.pathname.startsWith("/sales-representatives/");
  const superAdmin = mode === "managers";
  const canAccess = canManageUsers(user) && (!salesPage || superAdmin);
  const [selectedAccountRole, setSelectedAccountRole] = useState(
    salesPage ? "salesrepresentative" : "manager",
  );
  const isSalesAccount = superAdmin && selectedAccountRole === "salesrepresentative";
  const returnPath = salesPage || isSalesAccount ? "/sales-representatives" : "/users";

  const establishmentId = useMemo(
    () => getUserEstablishmentId(user),
    [user],
  );

  const [form, setForm] = useState(
    mode === "managers" ? managerEmptyForm : staffEmptyForm,
  );

  const [fieldErrors, setFieldErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);

  const [direction, setDirection] = useState(
    document.documentElement.dir || "ltr",
  );

  const isRtl = direction === "rtl";

  const title = !isEditMode && isSalesAccount
    ? translateFallback(t, "usersManagement.form.addSalesRepresentativePageTitle", "Add Sales Representative")
    : isEditMode && isSalesAccount
      ? translateFallback(t, "usersManagement.form.editSalesRepresentativePageTitle", "Edit Sales Representative")
    : isEditMode
    ? translateFallback(
        t,
        "usersManagement.form.editPageTitle",
        "Edit user",
      )
    : mode === "managers"
      ? translateFallback(
          t,
          "usersManagement.form.addManagerPageTitle",
          "Add manager",
        )
      : translateFallback(
          t,
          "usersManagement.form.addStaffPageTitle",
          "Add staff",
        );

  const description =
    isSalesAccount
      ? translateFallback(t, "usersManagement.form.salesPageDescription", "Create a sales account without assigning a restaurant.")
      : mode === "managers"
      ? translateFallback(
          t,
          "usersManagement.form.managerPageDescription",
          "Create the manager account and link it to its business and establishment.",
        )
      : translateFallback(
          t,
          "usersManagement.form.staffPageDescription",
          "Choose the staff role first, then complete the account information.",
        );

  // Keep this page synchronized with the global application direction.
  useEffect(() => {
    const updateDirection = () => {
      setDirection(document.documentElement.dir || "ltr");
    };

    updateDirection();

    const observer = new MutationObserver(updateDirection);

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["dir", "lang"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
  };

  // Load the selected manager or staff user.
  const loadUser = async () => {
    if (!isEditMode || !canAccess) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response =
        isSalesAccount
          ? await userManagementApi.getSalesRepresentative(userId)
          : mode === "managers"
            ? await userManagementApi.getManager(userId)
            : await userManagementApi.getStaffUser(
              establishmentId,
              userId,
            );

      const record = response.data.data || response.data || {};

      if (isSalesAccount) {
        setForm({
          ...managerEmptyForm,
          name: record.name || "",
          email: record.email || "",
          phone: record.phone || "",
          role: "salesrepresentative",
        });
      } else if (mode === "managers") {
        setForm(getManagerFormFromRecord(record));
      } else {
        setForm({
          name: record.name || "",
          email: record.email || "",
          phone: record.phone || "",
          role: record.role === "cuisini" ? "cuisini" : "waiter",
          password: "",
          password_confirmation: "",
        });
      }
    } catch (error) {
      showMessage(
        "error",
        getApiMessage(
          error,
          translateFallback(
            t,
            "usersManagement.messages.loadError",
            "Unable to load users.",
          ),
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedAccountRole(salesPage ? "salesrepresentative" : "manager");
    setForm(
      mode === "managers"
        ? { ...managerEmptyForm, role: salesPage ? "salesrepresentative" : "manager" }
        : { ...staffEmptyForm },
    );

    setFieldErrors({});
    setMessage(null);
  }, [mode, salesPage]);

  useEffect(() => {
    loadUser();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, mode, establishmentId, userId, canAccess, isSalesAccount]);

  useEffect(() => {
    return () => {
      if (form.logo_preview?.startsWith("blob:")) {
        URL.revokeObjectURL(form.logo_preview);
      }
    };
  }, [form.logo_preview]);

  // Update a form field and clear its validation error.
  const updateForm = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));

    setFieldErrors((current) => {
      if (!current[key]) return current;

      const nextErrors = { ...current };
      delete nextErrors[key];

      return nextErrors;
    });
  };

  // Handle establishment logo selection and preview.
  const handleLogoChange = (event) => {
    const file = event.target.files?.[0] || null;

    setForm((current) => {
      if (current.logo_preview?.startsWith("blob:")) {
        URL.revokeObjectURL(current.logo_preview);
      }

      return {
        ...current,
        logo: file,
        logo_preview: file
          ? URL.createObjectURL(file)
          : current.logo_preview,
      };
    });

    setFieldErrors((current) => {
      if (!current.logo) return current;

      const nextErrors = { ...current };
      delete nextErrors.logo;

      return nextErrors;
    });
  };

  // Validate required account fields before submitting.
  const validateForm = () => {
    const nextErrors = {};

    if (!form.name.trim()) {
      nextErrors.name = translateFallback(
        t,
        "usersManagement.validation.name",
        "Name is required.",
      );
    }

    if (!form.email.trim()) {
      nextErrors.email = translateFallback(
        t,
        "usersManagement.validation.email",
        "Email is required.",
      );
    }

    if (mode === "managers" && !isSalesAccount && !form.business_name.trim()) {
      nextErrors.business_name = translateFallback(
        t,
        "usersManagement.validation.businessName",
        "Business name is required.",
      );
    }

    if (!isEditMode && !form.password.trim()) {
      nextErrors.password = translateFallback(
        t,
        "usersManagement.validation.password",
        "Password is required.",
      );
    }

    if (
      form.password.trim() &&
      form.password !== form.password_confirmation
    ) {
      nextErrors.password_confirmation = translateFallback(
        t,
        "usersManagement.validation.passwordConfirmation",
        "Password confirmation does not match.",
      );
    }

    setFieldErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  // Build the API payload for manager or staff operations.
  const buildPayload = () => {
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || null,
    };

    if (isSalesAccount) {
      payload.role = "salesrepresentative";
    } else if (mode === "managers") {
      payload.establishment_id = form.establishment_id
        ? Number(form.establishment_id)
        : null;

      payload.business_name = form.business_name.trim();
      payload.default_language = form.default_language || "fr";

      payload.business_type =
        form.business_type || "restaurant";

      payload.business_email =
        form.business_email.trim() || null;

      payload.business_phone =
        form.business_phone.trim() || null;

      payload.business_whatsapp =
        form.business_whatsapp.trim() || null;

      payload.business_address =
        form.business_address.trim() || null;

      payload.business_description =
        form.business_description.trim() || null;

      if (isBrowserFile(form.logo)) {
        payload.logo = form.logo;
      }
    } else {
      payload.role = form.role;
    }

    if (!isEditMode || form.password.trim()) {
      payload.password = form.password;
      payload.password_confirmation =
        form.password_confirmation;
    }

    return payload;
  };

  // Save the current manager or staff account.
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);
      setMessage(null);

      const payload = buildPayload();

      if (isSalesAccount) {
        if (isEditMode) {
          await userManagementApi.updateSalesRepresentative(userId, payload);
        } else {
          await userManagementApi.createSalesRepresentative(payload);
        }
      } else if (mode === "managers") {
        if (isEditMode) {
          await userManagementApi.updateManager(
            userId,
            payload,
          );
        } else {
          await userManagementApi.createManager(payload);
        }
      } else if (isEditMode) {
        await userManagementApi.updateStaff(
          establishmentId,
          userId,
          payload,
        );
      } else {
        await userManagementApi.createStaff(
          establishmentId,
          payload,
        );
      }

      navigate(returnPath, {
        replace: true,
        state: {
          saved: true,
        },
      });
    } catch (error) {
      setFieldErrors(getApiFieldErrors(error));

      showMessage(
        "error",
        getApiMessage(
          error,
          translateFallback(
            t,
            "usersManagement.messages.saveError",
            "Unable to save user.",
          ),
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const renderRoleCard = (option, disabled = false) => {
    const Icon = option.icon;

    const selected =
      mode === "managers"
        ? selectedAccountRole === option.value
        : form.role === option.value;

    return (
      <button
        key={option.value}
        type="button"
        className={
          selected
            ? "users-form-role-card active"
            : "users-form-role-card"
        }
        disabled={disabled}
        onClick={() => {
          if (mode === "managers") {
            setSelectedAccountRole(option.value);
            updateForm("role", option.value);
          } else {
            updateForm("role", option.value);
          }
        }}
      >
        <span className="users-form-role-card__icon">
          <Icon size={25} />
        </span>

        <span className="users-form-role-card__copy">
          <strong>
            {translateFallback(
              t,
              `usersManagement.roles.${option.value}`,
              option.label,
            )}
          </strong>

          <small>
            {translateFallback(
              t,
              `usersManagement.roleDescriptions.${option.value}`,
              option.description,
            )}
          </small>
        </span>

        <span className="users-form-role-card__check">
          {selected && <Check size={16} />}
        </span>
      </button>
    );
  };

  if (!canAccess) {
    return (
      <main
        className={`users-form-page ${
          isRtl
            ? "users-form-page--rtl"
            : "users-form-page--ltr"
        }`}
        dir={direction}
      >
        <section className="users-form-blocked">
          <ShieldCheck size={34} />

          <h1>
            {translateFallback(
              t,
              "usersManagement.blocked.title",
              "Access restricted",
            )}
          </h1>

          <p>
            {translateFallback(
              t,
              "usersManagement.blocked.description",
              "Only super admin and manager accounts can manage users.",
            )}
          </p>
        </section>
      </main>
    );
  }

  return (
    <main
      className={`users-form-page ${
        isRtl
          ? "users-form-page--rtl"
          : "users-form-page--ltr"
      }`}
      dir={direction}
    >
      <header className="users-form-header">
        <button
          type="button"
          className="users-form-back-button"
          onClick={() => navigate(returnPath)}
        >
          {isRtl ? (
            <ArrowRight size={18} />
          ) : (
            <ArrowLeft size={18} />
          )}

          <span>
            {translateFallback(
              t,
              "common.back",
              "Back",
            )}
          </span>
        </button>

        <div className="users-form-header__copy">
          <p className="users-form-eyebrow">
            {mode === "managers"
              ? translateFallback(
                  t,
                  "usersManagement.eyebrow.superAdmin",
                  "Super admin control",
                )
              : translateFallback(
                  t,
                  "usersManagement.eyebrow.manager",
                  "Restaurant staff control",
                )}
          </p>

          <h1>{title}</h1>

          <span>{description}</span>
        </div>
      </header>

      {message && (
        <div
          className={`users-form-alert users-form-alert--${message.type}`}
          role="alert"
        >
          {message.type === "success" ? (
            <CheckCircle2 size={18} />
          ) : (
            <AlertCircle size={18} />
          )}

          <span>{message.text}</span>
        </div>
      )}

      {loading ? (
        <section className="users-form-loading-panel">
          <div className="users-form-loading">
            <Loader2 size={28} />

            <span>
              {translateFallback(
                t,
                "usersManagement.loading",
                "Loading users...",
              )}
            </span>
          </div>
        </section>
      ) : (
        <form
          className="users-form-shell"
          onSubmit={handleSubmit}
        >
          <section className="users-form-section users-form-section--role">
            <div className="users-form-section__title">
              <span className="users-form-section__icon">
                <ShieldCheck size={20} />
              </span>

              <div>
                <h2>
                  {translateFallback(
                    t,
                    "usersManagement.form.roleSection",
                    "Role access",
                  )}
                </h2>

                <p>
                  {isSalesAccount
                    ? translateFallback(t, "usersManagement.form.salesRoleDescription", "Sales representatives register establishments, subscriptions and QR print orders.")
                    : mode === "managers"
                    ? translateFallback(
                        t,
                        "usersManagement.form.managerRoleDescription",
                        "The created account will be a manager. Super admin manages this role only.",
                      )
                    : translateFallback(
                        t,
                        "usersManagement.form.staffRoleDescription",
                        "Select the staff role before adding the account details.",
                      )}
                </p>
              </div>
            </div>

            <div className="users-form-role-selector">
              {mode === "managers"
                ? salesPage
                  ? renderRoleCard(salesRoleOption, true)
                  : [managerRoleOption, salesRoleOption].map((option) =>
                      renderRoleCard(option, isEditMode),
                    )
                : staffRoleOptions.map((option) =>
                    renderRoleCard(option),
                  )}
            </div>
          </section>

          {mode === "managers" && !isSalesAccount && (
            <section className="users-form-section users-form-section--business">
              <div className="users-form-section__title">
                <span className="users-form-section__icon">
                  <Store size={20} />
                </span>

                <div>
                  <h2>
                    {translateFallback(
                      t,
                      "usersManagement.form.businessSection",
                      "Business and establishment information",
                    )}
                  </h2>

                  <p>
                    {translateFallback(
                      t,
                      "usersManagement.form.businessSectionDescription",
                      "These fields match the businesses and establishments tables linked to the manager.",
                    )}
                  </p>
                </div>
              </div>

              <div className="users-form-business-layout">
                <label
                  className={`users-form-logo-upload ${
                    getFieldError(
                      fieldErrors,
                      "logo",
                    )
                      ? "error"
                      : ""
                  }`}
                >
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleLogoChange}
                  />

                  <span className="users-form-logo-upload__preview">
                    {form.logo_preview ? (
                      <img
                        src={form.logo_preview}
                        alt={translateFallback(
                          t,
                          "usersManagement.form.logoAlt",
                          "Business logo",
                        )}
                      />
                    ) : (
                      <ImagePlus size={34} />
                    )}
                  </span>

                  <strong>
                    {translateFallback(
                      t,
                      "usersManagement.form.logo",
                      "Establishment logo",
                    )}
                  </strong>

                  <small>
                    {form.logo?.name ||
                      translateFallback(
                        t,
                        "usersManagement.form.logoHint",
                        "Stored in establishments.logo_path. PNG, JPG or WEBP up to 4 MB.",
                      )}
                  </small>

                  {getFieldError(
                    fieldErrors,
                    "logo",
                  ) && (
                    <em>
                      {getFieldError(
                        fieldErrors,
                        "logo",
                      )}
                    </em>
                  )}
                </label>

                <div className="users-form-grid users-form-grid--business">
                  <label
                    className={
                      getFieldError(
                        fieldErrors,
                        "business_name",
                      )
                        ? "error"
                        : ""
                    }
                  >
                    <span>
                      {translateFallback(
                        t,
                        "usersManagement.form.businessName",
                        "Business / establishment name",
                      )}
                    </span>

                    <input
                      value={form.business_name}
                      onChange={(event) =>
                        updateForm(
                          "business_name",
                          event.target.value,
                        )
                      }
                      placeholder={translateFallback(
                        t,
                        "usersManagement.form.businessNamePlaceholder",
                        "Example: Restaurant Marrakech",
                      )}
                    />

                    {getFieldError(
                      fieldErrors,
                      "business_name",
                    ) && (
                      <small>
                        {getFieldError(
                          fieldErrors,
                          "business_name",
                        )}
                      </small>
                    )}
                  </label>

                  <label>
                    <span>
                      {translateFallback(
                        t,
                        "usersManagement.form.businessType",
                        "Establishment type",
                      )}
                    </span>

                    <PrettySelect
                      value={form.business_type}
                      options={businessTypeOptions.map(
                        (option) => ({
                          ...option,
                          label: translateFallback(
                            t,
                            `usersManagement.businessTypes.${option.value}`,
                            option.label,
                          ),
                        }),
                      )}
                      onChange={(value) =>
                        updateForm(
                          "business_type",
                          value,
                        )
                      }
                      placeholder={translateFallback(
                        t,
                        "common.select",
                        "Select an option",
                      )}
                    />
                  </label>

                  <label>
                    <span>
                      {translateFallback(
                        t,
                        "usersManagement.form.defaultLanguage",
                        "Default language",
                      )}
                    </span>

                    <PrettySelect
                      value={form.default_language}
                      options={languageOptions}
                      onChange={(value) =>
                        updateForm(
                          "default_language",
                          value,
                        )
                      }
                      placeholder={translateFallback(
                        t,
                        "common.select",
                        "Select an option",
                      )}
                    />
                  </label>

                  <label>
                    <span>
                      {translateFallback(
                        t,
                        "usersManagement.form.businessEmail",
                        "Establishment email",
                      )}
                    </span>

                    <input
                      type="email"
                      dir="ltr"
                      value={form.business_email}
                      onChange={(event) =>
                        updateForm(
                          "business_email",
                          event.target.value,
                        )
                      }
                      placeholder="contact@restaurant.com"
                    />
                  </label>

                  <label>
                    <span>
                      {translateFallback(
                        t,
                        "usersManagement.form.businessPhone",
                        "Establishment phone",
                      )}
                    </span>

                    <input
                      dir="ltr"
                      value={form.business_phone}
                      onChange={(event) =>
                        updateForm(
                          "business_phone",
                          event.target.value,
                        )
                      }
                      placeholder="+212 600 000 000"
                    />
                  </label>

                  <label>
                    <span>WhatsApp</span>

                    <input
                      dir="ltr"
                      value={form.business_whatsapp}
                      onChange={(event) =>
                        updateForm(
                          "business_whatsapp",
                          event.target.value,
                        )
                      }
                      placeholder="+212 600 000 000"
                    />
                  </label>

                  <label className="users-form-grid__full">
                    <span>
                      {translateFallback(
                        t,
                        "usersManagement.form.businessAddress",
                        "Establishment address",
                      )}
                    </span>

                    <textarea
                      value={form.business_address}
                      onChange={(event) =>
                        updateForm(
                          "business_address",
                          event.target.value,
                        )
                      }
                      placeholder={translateFallback(
                        t,
                        "usersManagement.form.businessAddressPlaceholder",
                        "Full establishment address",
                      )}
                    />
                  </label>

                  <label className="users-form-grid__full">
                    <span>
                      {translateFallback(
                        t,
                        "usersManagement.form.businessDescription",
                        "Establishment description",
                      )}
                    </span>

                    <textarea
                      value={form.business_description}
                      onChange={(event) =>
                        updateForm(
                          "business_description",
                          event.target.value,
                        )
                      }
                      placeholder={translateFallback(
                        t,
                        "usersManagement.form.businessDescriptionPlaceholder",
                        "Short description shown on the public menu",
                      )}
                    />
                  </label>
                </div>
              </div>
            </section>
          )}

          <section className="users-form-section users-form-section--account">
            <div className="users-form-section__title">
              <span className="users-form-section__icon">
                <UserRound size={20} />
              </span>

              <div>
                <h2>
                  {translateFallback(
                    t,
                    "usersManagement.form.accountSection",
                    "Account information",
                  )}
                </h2>

                <p>
                  {translateFallback(
                    t,
                    "usersManagement.form.accountSectionDescription",
                    "These fields are saved in the users table.",
                  )}
                </p>
              </div>
            </div>

            <div className="users-form-grid">
              <label
                className={
                  getFieldError(
                    fieldErrors,
                    "name",
                  )
                    ? "error"
                    : ""
                }
              >
                <span>
                  {translateFallback(
                    t,
                    "usersManagement.form.name",
                    "Name",
                  )}
                </span>

                <input
                  value={form.name}
                  onChange={(event) =>
                    updateForm(
                      "name",
                      event.target.value,
                    )
                  }
                  placeholder={translateFallback(
                    t,
                    "usersManagement.form.namePlaceholder",
                    "Example: Manager Marrakech",
                  )}
                />

                {getFieldError(
                  fieldErrors,
                  "name",
                ) && (
                  <small>
                    {getFieldError(
                      fieldErrors,
                      "name",
                    )}
                  </small>
                )}
              </label>

              <label
                className={
                  getFieldError(
                    fieldErrors,
                    "email",
                  )
                    ? "error"
                    : ""
                }
              >
                <span>
                  {translateFallback(
                    t,
                    "usersManagement.form.email",
                    "Email",
                  )}
                </span>

                <input
                  type="email"
                  dir="ltr"
                  value={form.email}
                  onChange={(event) =>
                    updateForm(
                      "email",
                      event.target.value,
                    )
                  }
                  placeholder="example@email.com"
                />

                {getFieldError(
                  fieldErrors,
                  "email",
                ) && (
                  <small>
                    {getFieldError(
                      fieldErrors,
                      "email",
                    )}
                  </small>
                )}
              </label>

              <label>
                <span>
                  {translateFallback(
                    t,
                    "usersManagement.form.phone",
                    "Phone",
                  )}
                </span>

                <input
                  dir="ltr"
                  value={form.phone}
                  onChange={(event) =>
                    updateForm(
                      "phone",
                      event.target.value,
                    )
                  }
                  placeholder="+212 600 000 000"
                />
              </label>

              <label
                className={
                  getFieldError(
                    fieldErrors,
                    "password",
                  )
                    ? "error"
                    : ""
                }
              >
                <span>
                  {isEditMode
                    ? translateFallback(
                        t,
                        "usersManagement.form.newPassword",
                        "New password",
                      )
                    : translateFallback(
                        t,
                        "usersManagement.form.password",
                        "Password",
                      )}
                </span>

                <input
                  type="password"
                  value={form.password}
                  onChange={(event) =>
                    updateForm(
                      "password",
                      event.target.value,
                    )
                  }
                  placeholder={
                    isEditMode
                      ? translateFallback(
                          t,
                          "usersManagement.form.keepPasswordPlaceholder",
                          "Leave empty to keep current password",
                        )
                      : translateFallback(
                          t,
                          "usersManagement.form.passwordPlaceholder",
                          "Minimum 8 characters",
                        )
                  }
                />

                {getFieldError(
                  fieldErrors,
                  "password",
                ) && (
                  <small>
                    {getFieldError(
                      fieldErrors,
                      "password",
                    )}
                  </small>
                )}
              </label>

              <label
                className={
                  getFieldError(
                    fieldErrors,
                    "password_confirmation",
                  )
                    ? "error"
                    : ""
                }
              >
                <span>
                  {translateFallback(
                    t,
                    "usersManagement.form.passwordConfirmation",
                    "Confirm password",
                  )}
                </span>

                <input
                  type="password"
                  value={
                    form.password_confirmation
                  }
                  onChange={(event) =>
                    updateForm(
                      "password_confirmation",
                      event.target.value,
                    )
                  }
                  placeholder={translateFallback(
                    t,
                    "usersManagement.form.passwordConfirmationPlaceholder",
                    "Repeat password",
                  )}
                />

                {getFieldError(
                  fieldErrors,
                  "password_confirmation",
                ) && (
                  <small>
                    {getFieldError(
                      fieldErrors,
                      "password_confirmation",
                    )}
                  </small>
                )}
              </label>
            </div>
          </section>

          <div className="users-form-actions">
            <button
              type="button"
              className="users-form-cancel-button"
              onClick={() => navigate(returnPath)}
            >
              {translateFallback(
                t,
                "common.cancel",
                "Cancel",
              )}
            </button>

            <button
              type="submit"
              className="users-form-save-button"
              disabled={saving}
            >
              {saving ? (
                <Loader2 size={18} />
              ) : (
                <Save size={18} />
              )}

              <span>
                {isEditMode
                  ? translateFallback(
                      t,
                      "usersManagement.form.saveChanges",
                      "Save changes",
                    )
                  : translateFallback(
                      t,
                      "usersManagement.form.createUser",
                      "Create user",
                    )}
              </span>
            </button>
          </div>
        </form>
      )}
    </main>
  );
}

export default UserForm;